import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { apiService } from '../services/api';
import { ChargeExcelReport } from '../types';
import { downloadBlob } from '../utils/fileUtils';
import { apiErrorMessage } from '../utils/apiError';

interface ChargeExcelDialogProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
  canImport: boolean;
}

export default function ChargeExcelDialog({ open, onClose, onImported, canImport }: ChargeExcelDialogProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ChargeExcelReport | null>(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setError(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const runPreview = async (picked: File) => {
    setBusy(true);
    setError(null);
    try {
      setPreview(await apiService.importChargesExcel(picked, false));
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Vorschau fehlgeschlagen'));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const handleCommit = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      setPreview(await apiService.importChargesExcel(file, true));
      onImported();
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Import fehlgeschlagen'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>Chargen Excel</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Vorlage herunterladen, ausfüllen, dann prüfen. Nur .xlsx. Das Zertifikat und die
          Materialnummer müssen schon existieren. Die Chargen-Nr. wird erzeugt. Dieselbe
          Zertifikatsnummer mit derselben Materialnummer wird kein zweites Mal angelegt.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Button
            variant="outlined"
            onClick={async () => {
              downloadBlob(await apiService.downloadChargesTemplate(), 'chargen_vorlage.xlsx');
            }}
          >
            Vorlage
          </Button>
          <Button
            variant="outlined"
            onClick={async () => {
              downloadBlob(await apiService.exportChargesExcel(), 'chargen.xlsx');
            }}
          >
            Export
          </Button>
          <Button variant="contained" component="label" disabled={busy || !canImport}>
            Datei wählen
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              hidden
              onChange={(e) => {
                const picked = e.target.files?.[0];
                if (!picked) return;
                const name = picked.name.toLowerCase();
                if (
                  !name.endsWith('.xlsx') &&
                  picked.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                ) {
                  setError('Nur .xlsx-Dateien sind erlaubt (kein altes .xls-Format).');
                  setPreview(null);
                  setFile(null);
                  return;
                }
                setFile(picked);
                void runPreview(picked);
              }}
            />
          </Button>
        </Box>
        {preview && (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Neu {preview.created} · Duplikate {preview.skipped} · Warnungen {preview.warnings} ·
              Fehler {preview.errors}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Zeile</TableCell>
                  <TableCell>Chargen-Nr.</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Hinweis</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.rows.slice(0, 80).map((row) => (
                  <TableRow key={`${row.row}-${row.charge_number}`}>
                    <TableCell>{row.row}</TableCell>
                    <TableCell>{row.charge_number}</TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell>{row.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Schließen</Button>
        <Button variant="contained" onClick={handleCommit} disabled={!canImport || !file || busy || !preview}>
          Importieren
        </Button>
      </DialogActions>
    </Dialog>
  );
}
